import { useState } from 'react';
import { updateProfile, updatePassword, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { Layout } from '../components/layout/Layout';
import { Card, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { useAuth, auth } from '../context/AuthContext';
import { useToast } from '../components/ui/Toast';
import { useSettings } from '../hooks/useSettings';
import {
  User, Lock, Sliders, Info, Eye, EyeOff,
  Save, RotateCcw, CheckCircle2, Shield, Globe,
  Database, Zap,
} from 'lucide-react';

// ── Reusable field components ────────────────────────────────────────────────

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <label className="block text-xs font-semibold text-[#64748b] uppercase tracking-wider mb-1.5">
      {children}
    </label>
  );
}

function TextInput(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className={`w-full px-3.5 py-2.5 bg-[#1c1c2a] border border-[#252535] rounded-xl text-sm text-[#f1f5f9] placeholder-[#64748b] focus:outline-none focus:border-[#3b82f6] transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${props.className ?? ''}`}
    />
  );
}

function SectionDivider() {
  return <div className="border-t border-[#252535] my-6" />;
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-[#1c1c2a] last:border-0">
      <span className="text-sm text-[#64748b]">{label}</span>
      <span className="text-sm font-mono text-[#94a3b8] bg-[#1c1c2a] px-2.5 py-1 rounded-lg border border-[#252535] max-w-[55%] truncate text-right">
        {value}
      </span>
    </div>
  );
}

type Toggle = { label: string; description: string; checked: boolean; onChange: (v: boolean) => void };
function ToggleRow({ label, description, checked, onChange }: Toggle) {
  return (
    <div className="flex items-start justify-between gap-4 py-4 border-b border-[#1c1c2a] last:border-0">
      <div>
        <p className="text-sm font-medium text-[#f1f5f9]">{label}</p>
        <p className="text-xs text-[#64748b] mt-0.5">{description}</p>
      </div>
      <button
        onClick={() => onChange(!checked)}
        className={`relative shrink-0 w-10 h-5.5 rounded-full transition-colors duration-200 focus:outline-none ${
          checked ? 'bg-[#3b82f6]' : 'bg-[#252535]'
        }`}
        style={{ height: '22px', width: '40px' }}
      >
        <span
          className={`absolute top-0.5 left-0.5 w-4.5 h-4.5 rounded-full bg-white shadow transition-transform duration-200 ${
            checked ? 'translate-x-[18px]' : 'translate-x-0'
          }`}
          style={{ width: '18px', height: '18px' }}
        />
      </button>
    </div>
  );
}

// ── Tab definitions ──────────────────────────────────────────────────────────

const TABS = [
  { id: 'profile',     label: 'Profile',     icon: User },
  { id: 'preferences', label: 'Preferences', icon: Sliders },
  { id: 'security',    label: 'Security',    icon: Shield },
  { id: 'system',      label: 'System',      icon: Info },
] as const;

type TabId = typeof TABS[number]['id'];

// ── Profile tab ──────────────────────────────────────────────────────────────

function ProfileTab() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [displayName, setDisplayName] = useState(user?.displayName ?? '');
  const [saving, setSaving] = useState(false);

  const initials = (user?.displayName ?? user?.email ?? 'A')
    .split(' ')
    .map(w => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const handleSave = async () => {
    if (!auth.currentUser) return;
    try {
      setSaving(true);
      await updateProfile(auth.currentUser, { displayName: displayName.trim() || null });
      toast('Display name updated successfully', 'success');
    } catch {
      toast('Failed to update display name', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Avatar */}
      <div className="flex items-center gap-5">
        <div className="w-16 h-16 rounded-2xl gradient-blue flex items-center justify-center text-xl font-bold text-white shadow-lg shrink-0">
          {initials}
        </div>
        <div>
          <p className="font-semibold text-[#f1f5f9]">{user?.displayName || 'Admin'}</p>
          <p className="text-sm text-[#64748b]">{user?.email}</p>
          <span className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-[#22c55e] bg-[#22c55e]/10 border border-[#22c55e]/20 px-2 py-0.5 rounded-full">
            <CheckCircle2 className="w-3 h-3" />
            Admin
          </span>
        </div>
      </div>

      <SectionDivider />

      {/* Display name */}
      <div>
        <FieldLabel>Display Name</FieldLabel>
        <TextInput
          value={displayName}
          onChange={e => setDisplayName(e.target.value)}
          placeholder="Enter your display name"
        />
        <p className="text-xs text-[#64748b] mt-1.5">
          This name is shown in the admin panel header.
        </p>
      </div>

      {/* Email - read only */}
      <div>
        <FieldLabel>Email Address</FieldLabel>
        <TextInput value={user?.email ?? ''} disabled readOnly />
        <p className="text-xs text-[#64748b] mt-1.5">
          Email is managed by Firebase Authentication and cannot be changed here.
        </p>
      </div>

      <div className="flex justify-end">
        <Button variant="primary" onClick={handleSave} loading={saving} icon={<Save className="w-4 h-4" />}>
          Save Profile
        </Button>
      </div>
    </div>
  );
}

// ── Preferences tab ──────────────────────────────────────────────────────────

function PreferencesTab() {
  const { settings, updateSettings, resetSettings } = useSettings();
  const { toast } = useToast();

  const handleReset = () => {
    resetSettings();
    toast('Preferences reset to defaults', 'info');
  };

  return (
    <div className="space-y-6">
      {/* Table settings */}
      <div>
        <h3 className="text-sm font-semibold text-[#f1f5f9] mb-4 flex items-center gap-2">
          <Database className="w-4 h-4 text-[#3b82f6]" />
          Table Settings
        </h3>

        <div className="space-y-4">
          <div>
            <FieldLabel>Default Page Size</FieldLabel>
            <div className="flex gap-2 mt-1">
              {([10, 20, 50] as const).map(size => (
                <button
                  key={size}
                  onClick={() => updateSettings({ defaultPageSize: size })}
                  className={`px-4 py-2 rounded-xl text-sm font-medium border transition-all ${
                    settings.defaultPageSize === size
                      ? 'bg-[#3b82f6]/15 border-[#3b82f6] text-[#3b82f6]'
                      : 'bg-[#1c1c2a] border-[#252535] text-[#94a3b8] hover:border-[#3b82f6]/50'
                  }`}
                >
                  {size} rows
                </button>
              ))}
            </div>
            <p className="text-xs text-[#64748b] mt-1.5">
              Default number of rows shown in Users and Twins tables.
            </p>
          </div>

          <ToggleRow
            label="Compact Mode"
            description="Reduce row padding for denser tables - useful on smaller screens."
            checked={settings.compactMode}
            onChange={v => updateSettings({ compactMode: v })}
          />
        </div>
      </div>

      <SectionDivider />

      {/* Dashboard refresh */}
      <div>
        <h3 className="text-sm font-semibold text-[#f1f5f9] mb-4 flex items-center gap-2">
          <Zap className="w-4 h-4 text-[#a855f7]" />
          Dashboard Auto-Refresh
        </h3>

        <div>
          <FieldLabel>Refresh Interval</FieldLabel>
          <div className="flex flex-wrap gap-2 mt-1">
            {([
              { value: 0,   label: 'Off' },
              { value: 30,  label: '30s' },
              { value: 60,  label: '1 min' },
              { value: 300, label: '5 min' },
            ] as const).map(opt => (
              <button
                key={opt.value}
                onClick={() => updateSettings({ autoRefreshInterval: opt.value })}
                className={`px-4 py-2 rounded-xl text-sm font-medium border transition-all ${
                  settings.autoRefreshInterval === opt.value
                    ? 'bg-[#a855f7]/15 border-[#a855f7] text-[#a855f7]'
                    : 'bg-[#1c1c2a] border-[#252535] text-[#94a3b8] hover:border-[#a855f7]/50'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <p className="text-xs text-[#64748b] mt-1.5">
            Automatically reload dashboard stats. Set to Off to disable.
          </p>
        </div>
      </div>

      <SectionDivider />

      <div className="flex justify-end">
        <Button variant="ghost" onClick={handleReset} icon={<RotateCcw className="w-4 h-4" />}>
          Reset to Defaults
        </Button>
      </div>
    </div>
  );
}

// ── Security tab ─────────────────────────────────────────────────────────────

function SecurityTab() {
  const { user, logout } = useAuth();
  const { toast } = useToast();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      toast('New passwords do not match', 'error');
      return;
    }
    if (newPassword.length < 8) {
      toast('Password must be at least 8 characters', 'error');
      return;
    }
    if (!auth.currentUser || !user?.email) return;

    try {
      setSaving(true);
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(auth.currentUser, credential);
      await updatePassword(auth.currentUser, newPassword);
      toast('Password changed successfully', 'success');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      const code = err?.code ?? '';
      if (code === 'auth/wrong-password' || code === 'auth/invalid-credential') {
        toast('Current password is incorrect', 'error');
      } else {
        toast('Failed to change password. Please try again.', 'error');
      }
    } finally {
      setSaving(false);
    }
  };

  const lastSignIn = user?.metadata?.lastSignInTime
    ? new Date(user.metadata.lastSignInTime).toLocaleString()
    : '-';
  const createdAt = user?.metadata?.creationTime
    ? new Date(user.metadata.creationTime).toLocaleString()
    : '-';

  return (
    <div className="space-y-6">
      {/* Session info */}
      <div>
        <h3 className="text-sm font-semibold text-[#f1f5f9] mb-3 flex items-center gap-2">
          <Shield className="w-4 h-4 text-[#22c55e]" />
          Session Info
        </h3>
        <div className="bg-[#1c1c2a] rounded-xl border border-[#252535] px-4">
          <InfoRow label="Last sign-in" value={lastSignIn} />
          <InfoRow label="Account created" value={createdAt} />
          <InfoRow label="Auth provider" value="Email / Password" />
        </div>
      </div>

      <SectionDivider />

      {/* Change password */}
      <div>
        <h3 className="text-sm font-semibold text-[#f1f5f9] mb-4 flex items-center gap-2">
          <Lock className="w-4 h-4 text-[#f59e0b]" />
          Change Password
        </h3>

        <div className="space-y-4">
          <div>
            <FieldLabel>Current Password</FieldLabel>
            <div className="relative">
              <TextInput
                type={showCurrent ? 'text' : 'password'}
                value={currentPassword}
                onChange={e => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowCurrent(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748b] hover:text-[#94a3b8] transition-colors"
              >
                {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div>
            <FieldLabel>New Password</FieldLabel>
            <div className="relative">
              <TextInput
                type={showNew ? 'text' : 'password'}
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                placeholder="Minimum 8 characters"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowNew(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748b] hover:text-[#94a3b8] transition-colors"
              >
                {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {/* Strength indicator */}
            {newPassword.length > 0 && (
              <div className="mt-2 flex gap-1">
                {[1, 2, 3, 4].map(i => {
                  const strength = Math.min(4, Math.floor(newPassword.length / 3));
                  const color = strength <= 1 ? '#ef4444' : strength === 2 ? '#f59e0b' : strength === 3 ? '#3b82f6' : '#22c55e';
                  return (
                    <div
                      key={i}
                      className="flex-1 h-1 rounded-full transition-colors"
                      style={{ background: i <= strength ? color : '#252535' }}
                    />
                  );
                })}
              </div>
            )}
          </div>

          <div>
            <FieldLabel>Confirm New Password</FieldLabel>
            <TextInput
              type="password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              placeholder="Repeat new password"
            />
            {confirmPassword && newPassword !== confirmPassword && (
              <p className="text-xs text-[#ef4444] mt-1">Passwords do not match</p>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between pt-2">
        <Button variant="danger" onClick={logout} size="sm">
          Sign Out
        </Button>
        <Button
          variant="primary"
          onClick={handleChangePassword}
          loading={saving}
          disabled={!currentPassword || !newPassword || newPassword !== confirmPassword}
          icon={<Lock className="w-4 h-4" />}
        >
          Update Password
        </Button>
      </div>
    </div>
  );
}

// ── System tab ───────────────────────────────────────────────────────────────

function SystemTab() {
  const apiUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080';
  const env = import.meta.env.MODE ?? 'development';
  const isDev = env === 'development';

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-sm font-semibold text-[#f1f5f9] mb-3 flex items-center gap-2">
          <Globe className="w-4 h-4 text-[#3b82f6]" />
          API & Environment
        </h3>
        <div className="bg-[#1c1c2a] rounded-xl border border-[#252535] px-4">
          <InfoRow label="Environment" value={env} />
          <InfoRow label="API base URL" value={apiUrl} />
          <InfoRow label="Firebase project" value={import.meta.env.VITE_FIREBASE_PROJECT_ID} />
        </div>
      </div>

      <SectionDivider />

      <div>
        <h3 className="text-sm font-semibold text-[#f1f5f9] mb-3 flex items-center gap-2">
          <Info className="w-4 h-4 text-[#a855f7]" />
          Application
        </h3>
        <div className="bg-[#1c1c2a] rounded-xl border border-[#252535] px-4">
          <InfoRow label="App name" value="OBAET Admin" />
          <InfoRow label="Version" value="1.0.0" />
          <InfoRow label="Framework" value="React 19 + Vite + Tailwind 4" />
          <InfoRow label="Build mode" value={isDev ? 'Development' : 'Production'} />
        </div>
      </div>

      {isDev && (
        <>
          <SectionDivider />
          <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-[#f59e0b]/8 border border-[#f59e0b]/20">
            <Zap className="w-4 h-4 text-[#f59e0b] shrink-0" />
            <p className="text-xs text-[#f59e0b]">
              Running in <strong>development</strong> mode. API calls go to <code className="font-mono">{apiUrl}</code>.
            </p>
          </div>
        </>
      )}
    </div>
  );
}

// ── Settings page ────────────────────────────────────────────────────────────

export function Settings() {
  const [activeTab, setActiveTab] = useState<TabId>('profile');

  const TabContent = {
    profile:     <ProfileTab />,
    preferences: <PreferencesTab />,
    security:    <SecurityTab />,
    system:      <SystemTab />,
  }[activeTab];

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h2 className="text-2xl font-bold text-[#f8fafc]">Settings</h2>
          <p className="text-[#94a3b8] mt-1">Manage your account and application preferences</p>
        </div>

        <div className="flex flex-col sm:flex-row gap-6 items-start">
          {/* Sidebar tabs */}
          <nav className="sm:w-52 shrink-0">
            <div className="flex sm:flex-col gap-1">
              {TABS.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl text-sm font-medium transition-all w-full text-left ${
                    activeTab === tab.id
                      ? 'bg-[#3b82f6]/10 text-[#3b82f6] nav-item-active'
                      : 'text-[#94a3b8] hover:text-[#f1f5f9] hover:bg-[#1c1c2a]'
                  }`}
                >
                  <tab.icon className="w-4 h-4 shrink-0" />
                  <span className="hidden sm:inline">{tab.label}</span>
                </button>
              ))}
            </div>
          </nav>

          {/* Content panel */}
          <div className="flex-1 min-w-0 w-full">
            <Card>
              <CardHeader
                title={TABS.find(t => t.id === activeTab)!.label}
                subtitle={{
                  profile:     'Your admin profile information',
                  preferences: 'Customize the look and behavior of the panel',
                  security:    'Password and session management',
                  system:      'Technical details and environment information',
                }[activeTab]}
              />
              <div className="mt-4">{TabContent}</div>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
}
